import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { dlpMetaService, MetaSoftwareRow, MetaWebsiteRow } from '../../../services/dlp-meta.service';
import { cn } from '../../../lib/utils';
import {
  SectionHeader, SearchBar, FormCard, Fieldset, Field, TableShell, Th, Row, Td,
  LoadingState, EmptyState, Pagination, Input, Select, Badge, Button,
} from '../../ui';

const PAGE_LIMIT = 50;
type MetaKind = 'software' | 'website';
type AnyRow = MetaSoftwareRow & MetaWebsiteRow;

interface FormState {
  keyValue: string; // process_name | url
  valValue: string; // program_name | web_name
  productCompany: string; // software only
}
const EMPTY_FORM: FormState = { keyValue: '', valValue: '', productCompany: '' };

const CONFIG: Record<MetaKind, {
  label: string; title: string; desc: string;
  keyField: 'process_name' | 'url'; valField: 'program_name' | 'web_name';
  keyLabel: string; valLabel: string; hasCompany: boolean;
}> = {
  software: {
    label: '소프트웨어', title: 'DLP 소프트웨어 매핑', desc: '차단 소프트웨어의 프로세스명 → 프로그램명 매핑 관리',
    keyField: 'process_name', valField: 'program_name', keyLabel: '프로세스명', valLabel: '프로그램명', hasCompany: true,
  },
  website: {
    label: '웹사이트', title: 'DLP 웹사이트 매핑', desc: '차단 웹사이트의 URL → 사이트명 매핑 관리',
    keyField: 'url', valField: 'web_name', keyLabel: 'URL', valLabel: '사이트명', hasCompany: false,
  },
};

const MetaMappingSection: React.FC = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [kind, setKind] = useState<MetaKind>('software');
  const cfg = CONFIG[kind];

  const [rows, setRows] = useState<AnyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [mapped, setMapped] = useState<'all' | 'mapped' | 'unmapped'>('all');
  const [isLoading, setIsLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const load = useCallback(async (pg: number, k: MetaKind, s: string, m: 'all' | 'mapped' | 'unmapped') => {
    setIsLoading(true);
    try {
      const q = { search: s || undefined, mapped: m, page: pg, limit: PAGE_LIMIT };
      const res = k === 'software' ? await dlpMetaService.getSoftware(q) : await dlpMetaService.getWebsite(q);
      setRows(res.items as AnyRow[]);
      setTotal(res.total);
    } catch {
      toast.error('목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 단일 로딩 경로 — 필터 변경은 아래 핸들러에서 page=1로 리셋(중복 요청 방지)
  useEffect(() => { load(page, kind, search, mapped); }, [page, kind, search, mapped, load]);

  const switchTab = (k: MetaKind) => { if (k !== kind) { setKind(k); setShowForm(false); setSearch(''); setMapped('all'); setPage(1); } };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const handleMapped = (v: 'all' | 'mapped' | 'unmapped') => { setMapped(v); setPage(1); };

  const openCreate = () => { setIsEditing(false); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row: AnyRow) => {
    setIsEditing(true);
    setForm({
      keyValue: (row[cfg.keyField] as string) || '',
      valValue: (row[cfg.valField] as string) || '',
      productCompany: row.product_company || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (kind === 'software') {
        const dto = { processName: form.keyValue.trim(), programName: form.valValue.trim(), productCompany: form.productCompany.trim() || undefined };
        if (isEditing) await dlpMetaService.updateSoftware(dto);
        else await dlpMetaService.createSoftware(dto);
      } else {
        const dto = { url: form.keyValue.trim(), webName: form.valValue.trim() };
        if (isEditing) await dlpMetaService.updateWebsite(dto);
        else await dlpMetaService.createWebsite(dto);
      }
      toast.success(isEditing ? '매핑을 수정했습니다.' : '매핑을 추가했습니다.');
      setShowForm(false);
      load(page, kind, search, mapped);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {/* 세그먼트 탭 */}
      <div className="mb-4 inline-flex rounded-lg border border-border bg-muted/40 p-1">
        {(Object.keys(CONFIG) as MetaKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => switchTab(k)}
            className={cn(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
              kind === k ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {CONFIG[k].label}
          </button>
        ))}
      </div>

      <SectionHeader
        title={cfg.title}
        total={total}
        onAdd={canEdit ? openCreate : undefined}
        addLabel="+ 매핑 추가"
        description={cfg.desc}
      />

      {/* 검색/필터 */}
      <SearchBar>
        <Input
          placeholder={`${cfg.keyLabel} / ${cfg.valLabel} 검색`}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-[240px]"
        />
        <Select value={mapped} onChange={(e) => handleMapped(e.target.value as any)} className="w-auto">
          <option value="all">전체</option>
          <option value="unmapped">미매핑만</option>
          <option value="mapped">매핑됨</option>
        </Select>
      </SearchBar>

      {/* 등록/수정 카드 */}
      {showForm && (
        <FormCard
          title={`${cfg.label} 매핑 ${isEditing ? '수정' : '추가'}`}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
          isSaving={isSaving}
          saveLabel={isEditing ? '수정 저장' : '추가'}
        >
          <Fieldset legend="매핑 정보" accent minColWidth={220}>
            <Field label={cfg.keyLabel}>
              <Input
                value={form.keyValue}
                onChange={(e) => setForm((f) => ({ ...f, keyValue: e.target.value }))}
                placeholder={cfg.keyField === 'process_name' ? '예: SamsungFlow.exe' : '예: example.com'}
                disabled={isEditing}
                className={cn('font-mono', isEditing && 'opacity-60')}
              />
            </Field>
            <Field label={cfg.valLabel}>
              <Input
                value={form.valValue}
                onChange={(e) => setForm((f) => ({ ...f, valValue: e.target.value }))}
                placeholder={cfg.valField === 'program_name' ? '예: 삼성 Flow' : '예: 예시사이트'}
              />
            </Field>
            {cfg.hasCompany && (
              <Field label="제조사 (선택)">
                <Input value={form.productCompany} onChange={(e) => setForm((f) => ({ ...f, productCompany: e.target.value }))} placeholder="예: 삼성전자" />
              </Field>
            )}
          </Fieldset>
        </FormCard>
      )}

      {/* 테이블 */}
      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState text="매핑 데이터가 없습니다." />
      ) : (
        <TableShell
          headers={
            <>
              <Th>{cfg.keyLabel}</Th>
              <Th>{cfg.valLabel}</Th>
              <Th>카테고리</Th>
              {cfg.hasCompany && <Th>제조사</Th>}
              <Th>상태</Th>
              {canEdit && <Th>관리</Th>}
            </>
          }
          footer={<Pagination page={page} totalPages={totalPages} onPage={setPage} />}
        >
          {rows.map((row, idx) => {
            const keyVal = row[cfg.keyField] as string;
            const val = row[cfg.valField] as string | null;
            const isUnmapped = !val;
            return (
              <Row key={keyVal + idx} idx={idx}>
                <Td className="max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap font-mono">{keyVal}</Td>
                <Td className={cn('font-medium', isUnmapped && 'text-muted-foreground')}>{val || '—'}</Td>
                <Td className="text-muted-foreground">{row.category_name || '-'}</Td>
                {cfg.hasCompany && <Td className="text-muted-foreground">{row.product_company || '-'}</Td>}
                <Td>
                  {isUnmapped ? (
                    <Badge variant="warning">미매핑</Badge>
                  ) : row.needcheck === 'Y' ? (
                    <Badge variant="secondary">확인필요</Badge>
                  ) : (
                    <Badge variant="success">매핑됨</Badge>
                  )}
                </Td>
                {canEdit && (
                  <Td className="whitespace-nowrap">
                    <Button type="button" variant="outline" size="sm" className="h-7 px-2.5" onClick={() => openEdit(row)}>
                      {isUnmapped ? '매핑' : '수정'}
                    </Button>
                  </Td>
                )}
              </Row>
            );
          })}
        </TableShell>
      )}
    </div>
  );
};

export default MetaMappingSection;
